/**
 * Legal page copy — Privacy Policy and Terms of Service.
 *
 * RECOVERED CONTENT, GENERATED FILE. These pages were originally hand-written
 * into the compiled bundle (commit d95bf96 touched only
 * dist-landing/assets/index-BtaeqFD3.js), so they had no source and any
 * `pnpm build:landing` erased them. This module was produced by parsing the live
 * pages back into structured data, so they now rebuild from source like
 * everything else.
 *
 * Inline formatting is modelled as typed runs rather than HTML strings, so
 * rendering needs no dangerouslySetInnerHTML (see components/LegalPage.tsx).
 *
 * Edit the copy here — it is plain data. The generator was one-shot; there is no
 * script to re-run, and re-scraping production would now scrape this very file.
 */

/** A span of inline content inside a paragraph, heading or list item. */
export type LegalRun =
  | { k: 't'; v: string }
  | { k: 'strong'; v: string }
  | { k: 'link'; v: string; href: string };

/** One block of a legal document, in source order. */
export type LegalBlock =
  | { kind: 'h2'; runs: readonly LegalRun[] }
  | { kind: 'h3'; runs: readonly LegalRun[] }
  | { kind: 'p'; runs: readonly LegalRun[] }
  | { kind: 'ul'; items: readonly (readonly LegalRun[])[] };

export interface LegalDoc {
  /** Path this document is served at. */
  path: string;
  title: string;
  /** Legal entity + effective/updated dates, shown under the title. */
  meta: string;
  blocks: readonly LegalBlock[];
}

export const PRIVACY_POLICY: LegalDoc = {
  path: '/privacy-policy',
  title: 'Privacy Policy',
  meta: 'WisdomX - Brain on Demand Ltd Effective date: July 2026 | Last updated: July 2026',
  blocks: [
    { kind: 'h2', runs: [{ k: 't', v: '1. Who We Are' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'WisdomX - Brain on Demand Ltd is a company registered in England and Wales. We are the data controller for personal data collected through our websites and the Orbis platform.',
        },
      ],
    },
    { kind: 'p', runs: [{ k: 't', v: 'Contact us about data privacy:' }] },
    {
      kind: 'ul',
      items: [
        [
          { k: 't', v: 'Email:' },
          { k: 'link', v: 'hello@orbiswisdomx.com', href: 'mailto:hello@orbiswisdomx.com' },
        ],
        [{ k: 't', v: 'Subject line: Data Privacy Enquiry' }],
        [{ k: 't', v: 'Website: orbiswisdomx.com' }],
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '2. What Data We Collect' }] },
    { kind: 'p', runs: [{ k: 't', v: 'We collect the following categories of information:' }] },
    { kind: 'h3', runs: [{ k: 't', v: 'Contact and account information' }] },
    {
      kind: 'ul',
      items: [
        [{ k: 't', v: 'Name and job title' }],
        [{ k: 't', v: 'Work email address' }],
        [{ k: 't', v: 'Organisation name' }],
        [{ k: 't', v: 'Contact preferences' }],
      ],
    },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'We collect this when you request a demonstration, contact us, sign up for communications, or create a platform account.',
        },
      ],
    },
    { kind: 'h3', runs: [{ k: 't', v: 'Platform usage data' }] },
    {
      kind: 'ul',
      items: [
        [{ k: 't', v: 'Login activity and session information' }],
        [{ k: 't', v: 'Feature usage and interaction data' }],
        [{ k: 't', v: 'Support requests and correspondence' }],
      ],
    },
    {
      kind: 'p',
      runs: [{ k: 't', v: 'We collect this automatically when you use the Orbis platform.' }],
    },
    { kind: 'h3', runs: [{ k: 't', v: 'Technical data' }] },
    {
      kind: 'ul',
      items: [
        [{ k: 't', v: 'IP address' }],
        [{ k: 't', v: 'Browser type and version' }],
        [{ k: 't', v: 'Device type and operating system' }],
        [{ k: 't', v: 'Referring URLs and page visits' }],
      ],
    },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'We collect this automatically through standard web technologies when you visit our websites.',
        },
      ],
    },
    { kind: 'h3', runs: [{ k: 't', v: 'Customer estate data' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'When you connect Orbis to your organisation’s ITSM, CMDB, ERP, or other data sources, we process data about your IT assets on your behalf. This data is processed under a separate Data Processing Agreement and remains your property at all times. We act as a data processor for this data — not a data controller.',
        },
      ],
    },
    { kind: 'h3', runs: [{ k: 't', v: 'What we do NOT collect' }] },
    {
      kind: 'ul',
      items: [
        [{ k: 't', v: 'Payment card details (processed via third-party payment providers)' }],
        [{ k: 't', v: 'Sensitive personal data (as defined under UK GDPR Article 9)' }],
        [{ k: 't', v: 'Personal data from children under the age of 18' }],
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '3. How We Use Your Data' }] },
    {
      kind: 'ul',
      items: [
        [{ k: 't', v: 'To provide and operate the Orbis platform and associated services' }],
        [{ k: 't', v: 'To manage your account and respond to your requests' }],
        [{ k: 't', v: 'To send service communications, including updates and security notices' }],
        [{ k: 't', v: 'To improve the platform based on usage patterns and feedback' }],
        [{ k: 't', v: 'To comply with our legal obligations' }],
        [
          {
            k: 't',
            v: 'To send marketing communications where you have given consent or we have a legitimate interest — you can opt out at any time',
          },
        ],
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '4. Our Legal Basis for Processing (UK GDPR)' }] },
    {
      kind: 'ul',
      items: [
        [
          { k: 'strong', v: 'Contract:' },
          { k: 't', v: 'to provide services you have requested or entered into an agreement for' },
        ],
        [
          { k: 'strong', v: 'Legitimate interests:' },
          {
            k: 't',
            v: 'to operate and improve our platform, prevent fraud, and manage our business',
          },
        ],
        [
          { k: 'strong', v: 'Legal obligation:' },
          { k: 't', v: 'to comply with applicable laws and regulations' },
        ],
        [
          { k: 'strong', v: 'Consent:' },
          {
            k: 't',
            v: 'for marketing communications and cookies where consent is required — you may withdraw consent at any time',
          },
        ],
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '5. How We Store and Protect Your Data' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'Your data is stored securely on Microsoft Azure infrastructure located within the United Kingdom and European Economic Area. We apply the following security measures:',
        },
      ],
    },
    {
      kind: 'ul',
      items: [
        [{ k: 't', v: 'All data in transit is encrypted using TLS 1.3' }],
        [{ k: 't', v: 'All data at rest is encrypted using AES-256' }],
        [
          {
            k: 't',
            v: 'Access is controlled through Microsoft Entra ID with role-based permissions',
          },
        ],
        [
          {
            k: 't',
            v: 'API credentials and secrets are stored in Azure Key Vault — never in application code',
          },
        ],
        [{ k: 't', v: 'Regular security assessments and vulnerability scanning' }],
      ],
    },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'Your customer estate data is logically isolated within your own tenant environment. It is never accessible to other WisdomX customers.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '6. How Long We Keep Your Data' }] },
    {
      kind: 'ul',
      items: [
        [
          {
            k: 't',
            v: 'Account and contact data: retained for the duration of our relationship and for 3 years thereafter, unless you request earlier deletion',
          },
        ],
        [{ k: 't', v: 'Platform usage logs: retained for 12 months' }],
        [
          {
            k: 't',
            v: 'Customer estate data: retained for the duration of your subscription and for 90 days after termination, after which it is securely deleted',
          },
        ],
        [{ k: 't', v: 'Correspondence and support records: retained for 3 years' }],
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '7. Who We Share Your Data With' }] },
    { kind: 'p', runs: [{ k: 't', v: 'We do not sell your personal data to third parties.' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'We share data with the following categories of trusted third parties where necessary to deliver our services:',
        },
      ],
    },
    {
      kind: 'ul',
      items: [
        [{ k: 't', v: 'Microsoft Azure — cloud infrastructure and identity services' }],
        [
          {
            k: 't',
            v: 'Snapshot (our exclusive derisking partner) — for system archival services initiated by you',
          },
        ],
        [
          {
            k: 't',
            v: 'Email and communication service providers — to send you service and marketing communications',
          },
        ],
        [
          {
            k: 't',
            v: 'Analytics providers — to understand how our website and platform are used (aggregated and anonymised where possible)',
          },
        ],
      ],
    },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'All third parties are required to process data in accordance with our instructions and applicable data protection law. We enter into Data Processing Agreements with all processors.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '8. International Data Transfers' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'Your data is stored and processed within the United Kingdom and the European Economic Area. Where data is transferred outside these regions, we ensure appropriate safeguards are in place — including Standard Contractual Clauses approved by the Information Commissioner’s Office (ICO) — in accordance with UK GDPR requirements.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '9. Your Rights Under UK GDPR' }] },
    {
      kind: 'ul',
      items: [
        [
          { k: 'strong', v: 'Right to access:' },
          { k: 't', v: 'request a copy of the personal data we hold about you' },
        ],
        [
          { k: 'strong', v: 'Right to rectification:' },
          { k: 't', v: 'request correction of inaccurate or incomplete data' },
        ],
        [
          { k: 'strong', v: 'Right to erasure:' },
          { k: 't', v: 'request deletion of your personal data in certain circumstances' },
        ],
        [
          { k: 'strong', v: 'Right to restrict processing:' },
          { k: 't', v: 'request that we limit how we use your data' },
        ],
        [
          { k: 'strong', v: 'Right to data portability:' },
          { k: 't', v: 'receive your data in a structured, commonly used format' },
        ],
        [
          { k: 'strong', v: 'Right to object:' },
          {
            k: 't',
            v: 'object to processing based on legitimate interests or for direct marketing',
          },
        ],
        [
          { k: 'strong', v: 'Right to withdraw consent:' },
          {
            k: 't',
            v: 'where processing is based on consent, withdraw it at any time without affecting the lawfulness of prior processing',
          },
        ],
      ],
    },
    {
      kind: 'p',
      runs: [
        { k: 't', v: 'To exercise any of these rights, please contact us at' },
        { k: 'link', v: 'hello@orbiswisdomx.com', href: 'mailto:hello@orbiswisdomx.com' },
        {
          k: 't',
          v: 'with the subject line ‘Data Rights Request’. We will respond within 30 days.',
        },
      ],
    },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'If you are not satisfied with our response, you have the right to lodge a complaint with the Information Commissioner’s Office (ICO) at ico.org.uk.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '10. Cookies' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'Our websites use cookies to provide essential functionality and improve your experience. We use:',
        },
      ],
    },
    {
      kind: 'ul',
      items: [
        [
          { k: 'strong', v: 'Strictly necessary cookies:' },
          {
            k: 't',
            v: 'required for the website and platform to function — these cannot be disabled',
          },
        ],
        [
          { k: 'strong', v: 'Performance cookies:' },
          {
            k: 't',
            v: 'help us understand how visitors use our websites (used only with your consent)',
          },
        ],
        [
          { k: 'strong', v: 'Functional cookies:' },
          { k: 't', v: 'remember your preferences and settings' },
        ],
      ],
    },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'You can manage your cookie preferences through the cookie banner on our website or through your browser settings. Disabling non-essential cookies will not affect your ability to use the platform.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '11. Marketing Communications' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'We may send you information about WisdomX Orbis products, services, and events where you have given consent or where we have a legitimate interest in contacting you as a business professional.',
        },
      ],
    },
    {
      kind: 'p',
      runs: [{ k: 't', v: 'You can opt out of marketing communications at any time by:' }],
    },
    {
      kind: 'ul',
      items: [
        [{ k: 't', v: 'Clicking the ‘unsubscribe’ link in any marketing email' }],
        [
          { k: 't', v: 'Emailing' },
          { k: 'link', v: 'hello@orbiswisdomx.com', href: 'mailto:hello@orbiswisdomx.com' },
          { k: 't', v: 'with the subject line ‘Unsubscribe’' },
        ],
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '12. Links to Third-Party Websites' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'Our websites and documentation may contain links to third-party websites. We are not responsible for the privacy practices of those websites and encourage you to review their privacy policies before providing any personal data.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '13. Changes to This Policy' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'We may update this Privacy Policy from time to time. When we make material changes, we will notify you by updating the ‘Last updated’ date at the top of this page and, where appropriate, by sending you a notification.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '14. Contact Us' }] },
    {
      kind: 'p',
      runs: [
        { k: 't', v: 'WisdomX - Brain on Demand LtdEmail:' },
        { k: 'link', v: 'hello@orbiswisdomx.com', href: 'mailto:hello@orbiswisdomx.com' },
        { k: 't', v: 'Website: orbiswisdomx.com' },
      ],
    },
    {
      kind: 'p',
      runs: [
        { k: 't', v: 'For data security concerns specifically:' },
        { k: 'link', v: 'security@orbiswisdomx.com', href: 'mailto:security@orbiswisdomx.com' },
      ],
    },
    {
      kind: 'p',
      runs: [{ k: 't', v: '© 2026 WisdomX - Brain on Demand Ltd. All rights reserved.' }],
    },
  ],
};

export const TERMS_OF_SERVICE: LegalDoc = {
  path: '/terms',
  title: 'Terms of Service',
  meta: 'WisdomX - Brain on Demand Ltd Effective date: July 2026 | Last updated: July 2026',
  blocks: [
    { kind: 'h2', runs: [{ k: 't', v: '1. About WisdomX Orbis' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'WisdomX Orbis is a cloud-based Decision Intelligence platform for enterprise legacy system decommissioning, delivered as a Software-as-a-Service (SaaS) solution hosted on Microsoft Azure. The Platform connects to your existing ITSM, CMDB, ERP, and discovery systems to provide risk scoring, dependency mapping, and decommission decision support.',
        },
      ],
    },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'WisdomX - Brain on Demand Ltd is registered in England and Wales and operates from the United Kingdom.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '2. Eligibility' }] },
    {
      kind: 'ul',
      items: [
        [{ k: 't', v: 'Be at least 18 years of age' }],
        [
          {
            k: 't',
            v: 'Be acting on behalf of a business or organisation (the Platform is not intended for personal use)',
          },
        ],
        [{ k: 't', v: 'Have authority to enter into these Terms on behalf of your organisation' }],
        [
          {
            k: 't',
            v: 'Not be located in a jurisdiction where access to the Platform is prohibited by law',
          },
        ],
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '3. Account Registration and Security' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'Access to the Platform is managed through Microsoft Entra ID (Azure Active Directory). To use the Platform, your organisation must have a valid Microsoft Entra ID tenant.',
        },
      ],
    },
    { kind: 'p', runs: [{ k: 't', v: 'You are responsible for:' }] },
    {
      kind: 'ul',
      items: [
        [
          {
            k: 't',
            v: 'Ensuring that all users of the Platform within your organisation comply with these Terms',
          },
        ],
        [{ k: 't', v: 'Maintaining the security of user credentials and access permissions' }],
        [
          { k: 't', v: 'Promptly notifying us at' },
          { k: 'link', v: 'security@orbiswisdomx.com', href: 'mailto:security@orbiswisdomx.com' },
          { k: 't', v: 'if you become aware of any unauthorised access to your account' },
        ],
        [{ k: 't', v: 'All activity that occurs under your account' }],
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '4. Permitted Use' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'Subject to these Terms and payment of applicable fees, we grant you a limited, non-exclusive, non-transferable licence to access and use the Platform during your subscription period for your organisation’s internal business purposes.',
        },
      ],
    },
    {
      kind: 'ul',
      items: [
        [{ k: 't', v: 'Connect your organisation’s data sources and generate estate maps' }],
        [{ k: 't', v: 'Analyse and score legacy systems for decommission readiness' }],
        [{ k: 't', v: 'Review decommission recommendations and record approval decisions' }],
        [{ k: 't', v: 'Generate reports and export data for internal use' }],
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '5. Prohibited Use' }] },
    {
      kind: 'ul',
      items: [
        [
          {
            k: 't',
            v: 'Use the Platform for any unlawful purpose or in violation of any applicable law or regulation',
          },
        ],
        [
          {
            k: 't',
            v: 'Attempt to gain unauthorised access to any part of the Platform or its underlying infrastructure',
          },
        ],
        [
          {
            k: 't',
            v: 'Reverse engineer, decompile, disassemble, or attempt to derive the source code of the Platform',
          },
        ],
        [
          {
            k: 't',
            v: 'Copy, reproduce, distribute, or create derivative works of the Platform or its content without our written permission',
          },
        ],
        [
          {
            k: 't',
            v: 'Use the Platform to process data on behalf of third parties without a separate written agreement with us',
          },
        ],
        [
          {
            k: 't',
            v: 'Transmit any malicious code, viruses, or disruptive software through the Platform',
          },
        ],
        [
          {
            k: 't',
            v: 'Use automated means to access the Platform in a manner that exceeds reasonable use or interferes with its operation',
          },
        ],
        [
          {
            k: 't',
            v: 'Resell, sublicense, or otherwise make the Platform available to third parties without our written consent',
          },
        ],
        [
          {
            k: 't',
            v: 'Remove or alter any proprietary notices, labels, or branding on the Platform',
          },
        ],
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '6. Your Data' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'You retain ownership of all data you submit to or generate through the Platform (‘Customer Data’). You grant WisdomX a limited licence to process Customer Data solely for the purpose of providing the Service to you.',
        },
      ],
    },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'You are responsible for ensuring you have the right to submit Customer Data to the Platform, the accuracy and legality of Customer Data, and obtaining any necessary consents from individuals whose data you submit.',
        },
      ],
    },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'We process Customer Data in accordance with our Privacy Policy and, where applicable, a separate Data Processing Agreement. We will not use Customer Data for any purpose other than providing the Service.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '7. Intellectual Property' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'WisdomX Orbis, including its software, algorithms, reasoning models, decision frameworks, user interface, documentation, and all associated intellectual property, is owned by WisdomX - Brain on Demand Ltd or its licensors. Nothing in these Terms transfers any intellectual property rights to you.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '8. Confidentiality' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'Each party may have access to confidential information of the other in connection with these Terms. Each party agrees to keep the other party’s confidential information strictly confidential, use it only for the purposes of these Terms, not disclose it to any third party without prior written consent, and protect it using at least the same care as it uses for its own confidential information.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '9. Subscription, Fees and Payment' }] },
    {
      kind: 'ul',
      items: [
        [
          {
            k: 't',
            v: 'Subscriptions are billed annually in advance unless otherwise agreed in writing',
          },
        ],
        [
          {
            k: 't',
            v: 'Fees are non-refundable except where required by law or as expressly stated in your agreement',
          },
        ],
        [
          {
            k: 't',
            v: 'We reserve the right to change pricing with 60 days’ written notice prior to your renewal date',
          },
        ],
        [
          {
            k: 't',
            v: 'If payment is not received when due, we may suspend access to the Platform after providing reasonable notice',
          },
        ],
        [
          {
            k: 't',
            v: 'Where the Platform is purchased through the Azure Marketplace, Microsoft’s Marketplace terms apply to billing and payment',
          },
        ],
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '10. Diagnostic Engagements' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'We offer Diagnostic Engagement services separate from or prior to a Platform subscription. These are fixed-fee professional services engagements. The specific scope, deliverables, and terms of any Diagnostic Engagement will be set out in a separate Statement of Work.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '11. Availability and Service Levels' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'We aim to provide a reliable service and will use commercially reasonable efforts to maintain Platform availability in accordance with the service level targets set out in your subscription agreement. We do not guarantee uninterrupted or error-free access to the Platform. The Platform is provided on Microsoft Azure infrastructure. Azure service incidents beyond our reasonable control may affect availability.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '12. Snapshot Partner Services' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'WisdomX Orbis integrates with Snapshot, our exclusive derisking partner, to provide system archival services. Snapshot is an independent third-party service provider. Snapshot’s own terms of service and privacy policy apply to their services. WisdomX is not liable for the performance or availability of Snapshot’s services.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '13. Disclaimers' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'The Platform is provided ‘as is’ and ‘as available’. To the fullest extent permitted by applicable law, we disclaim all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, or non-infringement, and warranties that the Platform will be error-free or uninterrupted.',
        },
      ],
    },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'Decommission decisions are made by your organisation. WisdomX Orbis provides decision intelligence — the final decision to decommission any system rests with your authorised personnel. We are not liable for the consequences of any decommission decision made using the Platform.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '14. Limitation of Liability' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'To the fullest extent permitted by applicable law, neither party shall be liable for any indirect, incidental, special, consequential, or punitive damages. Our total aggregate liability to you shall not exceed the total fees paid by you to us in the 12 months preceding the event giving rise to the claim. Nothing in these Terms limits liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded under applicable law.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '15. Indemnification' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'You agree to indemnify and hold harmless WisdomX - Brain on Demand Ltd, its directors, employees, and agents from and against any claims, losses, damages, and expenses (including reasonable legal fees) arising out of your use of the Platform in violation of these Terms, your Customer Data, or your violation of any applicable law or regulation.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '16. Term and Termination' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'These Terms remain in effect for the duration of your subscription. On termination, your access to the Platform will cease, your Customer Data will remain available for export for 90 days, and after 90 days Customer Data will be securely and permanently deleted. All fees due up to the termination date remain payable.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '17. Changes to the Service' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'We reserve the right to modify, update, or discontinue features of the Platform with reasonable notice. Where a change materially and adversely affects your use of the Platform, you may terminate your subscription with 30 days’ notice.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '18. Changes to These Terms' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'We may update these Terms from time to time. We will notify you of material changes by updating the ‘Last updated’ date and providing notice through the Platform or by email. Continued use of the Platform after the effective date of updated Terms constitutes acceptance of the changes.',
        },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '19. Governing Law and Disputes' }] },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'These Terms are governed by and construed in accordance with the laws of England and Wales. Any dispute arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.',
        },
      ],
    },
    {
      kind: 'p',
      runs: [
        {
          k: 't',
          v: 'We encourage you to contact us in the first instance to resolve any dispute informally:',
        },
        { k: 'link', v: 'hello@orbiswisdomx.com', href: 'mailto:hello@orbiswisdomx.com' },
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '20. General' }] },
    {
      kind: 'ul',
      items: [
        [
          { k: 'strong', v: 'Entire agreement:' },
          {
            k: 't',
            v: 'these Terms, together with any applicable order form, Data Processing Agreement, and Azure Marketplace terms, constitute the entire agreement between the parties',
          },
        ],
        [
          { k: 'strong', v: 'Severability:' },
          {
            k: 't',
            v: 'if any provision is found invalid or unenforceable, the remaining provisions continue in full force',
          },
        ],
        [
          { k: 'strong', v: 'Waiver:' },
          {
            k: 't',
            v: 'failure to enforce any provision does not constitute a waiver of our right to enforce it in the future',
          },
        ],
        [
          { k: 'strong', v: 'Assignment:' },
          { k: 't', v: 'you may not assign your rights without our prior written consent' },
        ],
        [
          { k: 'strong', v: 'Force majeure:' },
          {
            k: 't',
            v: 'neither party shall be liable for failure or delay caused by circumstances beyond their reasonable control',
          },
        ],
        [
          { k: 'strong', v: 'Notices:' },
          { k: 't', v: 'formal notices should be sent to' },
          { k: 'link', v: 'hello@orbiswisdomx.com', href: 'mailto:hello@orbiswisdomx.com' },
        ],
      ],
    },
    { kind: 'h2', runs: [{ k: 't', v: '21. Contact Us' }] },
    {
      kind: 'p',
      runs: [
        { k: 't', v: 'WisdomX - Brain on Demand LtdEmail:' },
        { k: 'link', v: 'hello@orbiswisdomx.com', href: 'mailto:hello@orbiswisdomx.com' },
        { k: 't', v: 'Website: orbiswisdomx.com' },
      ],
    },
    {
      kind: 'p',
      runs: [{ k: 't', v: '© 2026 WisdomX - Brain on Demand Ltd. All rights reserved.' }],
    },
  ],
};

export const LEGAL_DOCS: readonly LegalDoc[] = [PRIVACY_POLICY, TERMS_OF_SERVICE];
